import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Banknote,
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  FileSpreadsheet,
  Phone,
  RefreshCw,
  Search,
  User,
  Users,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { ContactNameLine } from '@/components/crm/CallerName';
import { fetchCallsInRange, fetchExtensionCDR, fetchExtensions } from '@/services/api';
import { useModuleAccess } from '@/lib/useModuleAccess';
import type { CallRecord, Extension } from '@/types';
import { dispositionBucket } from '@/utils/callRecords';
import {
  CATEGORY_LABELS,
  STANDARD_RATES,
  formatRand,
  isOutboundCall,
  summariseCosts,
  type CallRates,
  type DestinationCategory,
} from '@/lib/callCosts';
import {
  ALL_EXTENSIONS,
  CALL_COLUMNS_KEY,
  CALL_DEFAULT_SORT,
  EXTENSION_COLUMNS_KEY,
  EXTENSION_DEFAULT_SORT,
  applyCallFilter,
  callFooter,
  costCalls,
  costCallColumns,
  costExport,
  costExtensionColumns,
  costExtensionLabel,
  costNote,
  extensionCostRows,
  extensionFooter,
  formatDateTime,
  formatDuration,
  formatDurationHMS,
  formatRate,
  type CallFilter,
  type CostedCall,
  type ExtensionCostRow,
} from '@/lib/reports/callCostReport';
import toast from 'react-hot-toast';
import { PartyLines } from './ExtensionDetailReport';
import { compareExtensions, getDatePresetRange, type DatePreset } from './reportDates';
import { ColumnPicker, ReportTable } from './ReportTable';
import { sortRows, useColumnSettings, type ReportColumn } from './reportColumns';
import { loadPdfAssets } from '@/lib/pdf/pdfAssets';
import { ScheduleReportButton } from './ScheduleReportButton';

function parseRate(value: string): number {
  const rate = Number(value.replace(',', '.'));
  return Number.isFinite(rate) && rate > 0 ? rate : 0;
}

const statusStyles = {
  answered: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  noAnswer: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  busy: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  failed: 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400',
} as const;

const categoryStyles: Record<DestinationCategory, string> = {
  mobile: 'text-emerald-700 dark:text-emerald-400',
  landline: 'text-sky-700 dark:text-sky-400',
  tollFree: 'text-gray-500 dark:text-gray-400',
  international: 'text-amber-700 dark:text-amber-400',
  other: 'text-amber-700 dark:text-amber-400',
};

/** On-screen cells for the shared columns, by column id; the others show their text. */
function withCells<Row>(
  columns: ReportColumn<Row>[],
  cells: Record<string, (row: Row) => ReactNode>
): ReportColumn<Row>[] {
  return columns.map((column) => (cells[column.id] ? { ...column, cell: cells[column.id] } : column));
}

export function CallCostReport() {
  const { session } = useModuleAccess('reporting');
  const customerName = session?.customer?.name;
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [loadingExtensions, setLoadingExtensions] = useState(true);
  const [selected, setSelected] = useState<string>(ALL_EXTENSIONS);
  const [showExtensionDropdown, setShowExtensionDropdown] = useState(false);
  const [extensionSearch, setExtensionSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeDatePreset, setActiveDatePreset] = useState<DatePreset | null>(null);
  const [mobileRate, setMobileRate] = useState(STANDARD_RATES.mobilePerMinute.toFixed(2));
  const [landlineRate, setLandlineRate] = useState(STANDARD_RATES.landlinePerMinute.toFixed(2));
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<CallRecord[]>([]);
  const [report, setReport] = useState<{ extension: string; startDate: string; endDate: string } | null>(null);
  const [callFilter, setCallFilter] = useState<CallFilter>('outbound');
  // Which PDF is being built: 'report' (the buttons at the top) or an extension number
  const [exportingPdf, setExportingPdf] = useState<string | null>(null);
  // In "All extensions": the extension drilled into, from the "Cost by extension" table
  const [focus, setFocus] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchExtensions()
      .then((exts) => setExtensions([...exts].sort(compareExtensions)))
      .catch((error) => {
        toast.error('Failed to load extensions');
        console.error(error);
      })
      .finally(() => setLoadingExtensions(false));
  }, []);

  const extensionNames = useMemo(
    () =>
      new Map(
        extensions.map((ext) => [ext.number, ext.display_name || ext.username || ext.number])
      ),
    [extensions]
  );

  const extensionLabel = useCallback(
    (number: string) => costExtensionLabel(number, extensionNames),
    [extensionNames]
  );

  const filteredExtensions = useMemo(() => {
    const query = extensionSearch.trim().toLowerCase();
    if (!query) return extensions;
    return extensions.filter((ext) =>
      [ext.number, ext.display_name, ext.username, ext.ext_name, ext.first_name, ext.last_name].some(
        (value) => value?.toLowerCase().includes(query)
      )
    );
  }, [extensionSearch, extensions]);

  const rates: CallRates = useMemo(
    () => ({ mobilePerMinute: parseRate(mobileRate), landlinePerMinute: parseRate(landlineRate) }),
    [mobileRate, landlineRate]
  );

  const costedCalls: CostedCall[] = useMemo(
    () => costCalls(records, rates, report?.extension ?? ALL_EXTENSIONS),
    [records, rates, report]
  );

  const allMode = report?.extension === ALL_EXTENSIONS;
  const scope = allMode ? focus : null;
  const showExtensionColumn = allMode && !scope;

  const scopedCalls = useMemo(
    () => (scope ? costedCalls.filter((c) => c.extension === scope) : costedCalls),
    [costedCalls, scope]
  );

  const summary = useMemo(
    () => summariseCosts(scopedCalls.map((c) => c.record), rates),
    [scopedCalls, rates]
  );

  const extensionRows: ExtensionCostRow[] = useMemo(
    () => extensionCostRows(costedCalls, extensionLabel),
    [costedCalls, extensionLabel]
  );

  const openExtension = useCallback((extension: string | null) => {
    setFocus(extension);
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const callColumns = useMemo(
    (): ReportColumn<CostedCall>[] =>
      withCells(costCallColumns({ showExtensionColumn, extensionLabel }), {
        time: ({ record }) => (
          <span className="flex items-center gap-2 whitespace-nowrap">
            <Clock className="w-3.5 h-3.5 shrink-0 text-gray-400" />
            <span className="truncate">{formatDateTime(record.time, record.timestamp)}</span>
          </span>
        ),
        extension: ({ extension }) => (
          <button
            type="button"
            onClick={() => openExtension(extension)}
            title={`Open ${extensionLabel(extension)}`}
            className="block max-w-full truncate text-left text-emerald-700 dark:text-emerald-400 hover:underline"
          >
            {extensionLabel(extension)}
          </button>
        ),
        type: ({ record }) => <span className="text-gray-600 dark:text-gray-300">{record.call_type}</span>,
        from: ({ record }) => <PartyLines value={record.call_from} />,
        to: ({ record }) => (
          <div className="min-w-0">
            <div className="truncate font-mono" title={record.call_to}>
              {record.call_to || '-'}
            </div>
            <ContactNameLine value={record.call_to} />
          </div>
        ),
        callerId: ({ record }) => <span className="font-mono">{record.dod_number || '-'}</span>,
        trunk: ({ record }) => (
          <span className="block truncate" title={record.dst_trunk || record.src_trunk}>
            {record.dst_trunk || record.src_trunk || '-'}
          </span>
        ),
        status: ({ record }) => (
          <span
            className={`inline-flex max-w-full truncate px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[dispositionBucket(record)]}`}
          >
            {record.disposition || 'Unknown'}
          </span>
        ),
        destination: ({ record, cost }) =>
          cost.category ? (
            <span className={categoryStyles[cost.category]}>{CATEGORY_LABELS[cost.category]}</span>
          ) : (
            <span className="text-gray-400">{record.call_type}</span>
          ),
        ring: ({ record }) => <span className="tabular-nums">{formatDuration(Math.round(record.ring_duration || 0))}</span>,
        talk: ({ record }) => <span className="tabular-nums">{formatDuration(Math.round(record.talk_duration || 0))}</span>,
        rate: ({ cost }) => (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {cost.category && cost.billableSeconds > 0 ? formatRate(cost.ratePerMinute) : ''}
          </span>
        ),
        cost: ({ cost }) => (
          <span className="font-semibold tabular-nums">{cost.category ? formatRand(cost.cost) : '—'}</span>
        ),
      }),
    [showExtensionColumn, extensionLabel, openExtension]
  );

  const extensionColumns = useMemo(
    (): ReportColumn<ExtensionCostRow>[] =>
      costExtensionColumns().map((column) => ({
        ...column,
        cell:
          column.id === 'extension'
            ? (row: ExtensionCostRow) => (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openExtension(row.extension);
                  }}
                  className="inline-flex max-w-full items-center gap-1 font-medium text-emerald-700 dark:text-emerald-400 hover:underline text-left"
                >
                  <span className="truncate">{row.label}</span>
                  <ChevronRight className="w-4 h-4 shrink-0" />
                </button>
              )
            : (row: ExtensionCostRow) => (
                <span className={`tabular-nums ${column.id === 'totalCost' ? 'font-semibold' : ''}`}>
                  {column.text(row)}
                </span>
              ),
      })),
    [openExtension]
  );

  const callTable = useColumnSettings(CALL_COLUMNS_KEY, callColumns, CALL_DEFAULT_SORT);
  const extensionTable = useColumnSettings(EXTENSION_COLUMNS_KEY, extensionColumns, EXTENSION_DEFAULT_SORT);

  const visibleCalls = useMemo(() => applyCallFilter(scopedCalls, callFilter), [scopedCalls, callFilter]);
  const sortedCalls = useMemo(
    () => sortRows(visibleCalls, callTable.sortColumn, callTable.sort.dir),
    [visibleCalls, callTable.sortColumn, callTable.sort.dir]
  );
  const sortedExtensionRows = useMemo(
    () => sortRows(extensionRows, extensionTable.sortColumn, extensionTable.sort.dir),
    [extensionRows, extensionTable.sortColumn, extensionTable.sort.dir]
  );
  const scopedOutboundCount = scopedCalls.filter((c) => isOutboundCall(c.record)).length;

  const applyDatePreset = (preset: DatePreset) => {
    const range = getDatePresetRange(preset);
    setActiveDatePreset(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const generateReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select start and end dates');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    setLoading(true);
    setReport(null);
    setFocus(null);
    try {
      const start = `${startDate.replace(/-/g, '/')} 00:00:00`;
      const end = `${endDate.replace(/-/g, '/')} 23:59:59`;
      const result =
        selected === ALL_EXTENSIONS
          ? await fetchCallsInRange(start, end)
          : await fetchExtensionCDR(selected, start, end);
      setRecords(result);
      setReport({ extension: selected, startDate, endDate });
      const outbound = result.filter(isOutboundCall).length;
      if (result.length === 0) toast('No call records found for this period', { icon: 'ℹ️' });
      else toast.success(`Found ${result.length} calls (${outbound} outbound)`);
    } catch (error) {
      toast.error('Failed to generate report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /** The report, or one extension of it, as the shared CSV/PDF builder sees it. */
  const exportFor = async (extension: string | null, withAssets: boolean) =>
    costExport({
      records,
      reportExtension: report?.extension ?? ALL_EXTENSIONS,
      focus: extension,
      rates,
      callFilter,
      callColumns: callTable.stored,
      extensionColumns: extensionTable.stored,
      extensionNames,
      startDate: report?.startDate ?? '',
      endDate: report?.endDate ?? '',
      customerName,
      assets: withAssets ? await loadPdfAssets() : undefined,
    });

  const download = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  /** CSV of the report, or of one extension when given, with the chosen columns and sort. */
  const exportCsv = async (extension: string | null) => {
    if (!report) return;
    const files = await exportFor(extension, false);
    download(new Blob([files.csv()], { type: 'text/csv;charset=utf-8' }), `${files.fileBase}.csv`);
  };

  /** PDF of the report, or of one extension when given, with the chosen columns and sort. */
  const exportPdf = async (extension: string | null) => {
    if (!report) return;
    setExportingPdf(extension ?? 'report');
    try {
      const files = await exportFor(extension, true);
      files.pdf().save(`${files.fileBase}.pdf`);
      toast.success('PDF exported successfully!');
    } catch (error) {
      toast.error('Failed to export PDF');
      console.error(error);
    } finally {
      setExportingPdf(null);
    }
  };

  const summaryCards = [
    {
      label: 'Total cost',
      value: formatRand(summary.total.cost),
      detail: `${summary.total.calls} charged calls`,
      className: 'bg-emerald-50 dark:bg-emerald-900/20',
      valueClass: 'text-emerald-700 dark:text-emerald-400',
    },
    {
      label: `Mobile @ ${formatRand(rates.mobilePerMinute)}/min`,
      value: formatRand(summary.byCategory.mobile.cost),
      detail: `${summary.byCategory.mobile.calls} calls · ${formatDurationHMS(summary.byCategory.mobile.seconds)}`,
      className: 'bg-green-50 dark:bg-green-900/20',
      valueClass: 'text-green-700 dark:text-green-400',
    },
    {
      label: `Landline @ ${formatRand(rates.landlinePerMinute)}/min`,
      value: formatRand(summary.byCategory.landline.cost),
      detail: `${summary.byCategory.landline.calls} calls · ${formatDurationHMS(summary.byCategory.landline.seconds)}`,
      className: 'bg-sky-50 dark:bg-sky-900/20',
      valueClass: 'text-sky-700 dark:text-sky-400',
    },
    {
      label: 'Charged talk time',
      value: formatDurationHMS(summary.total.seconds),
      detail: `${scopedOutboundCount} outbound calls in total`,
      className: 'bg-purple-50 dark:bg-purple-900/20',
      valueClass: 'text-purple-700 dark:text-purple-400',
    },
  ];

  const callTotals = callFooter(visibleCalls);
  const extensionTotals = extensionFooter(summary);

  const inputClass =
    'w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-emerald-500';

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
            <Banknote className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Call Cost Report</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Detailed call records with the rand value of each outbound call, billed per second
            </p>
          </div>
          <ScheduleReportButton
            kind="costs"
            className="ml-auto"
            filters={{
              extension: selected,
              callFilter,
              mobileRate: rates.mobilePerMinute,
              landlineRate: rates.landlinePerMinute,
              callColumns: callTable.stored,
              extensionColumns: extensionTable.stored,
            }}
            dateRange={activeDatePreset ?? undefined}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Extension
            </label>
            <button
              type="button"
              onClick={() => {
                setShowExtensionDropdown(!showExtensionDropdown);
                if (showExtensionDropdown) setExtensionSearch('');
              }}
              disabled={loadingExtensions}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-left text-gray-900 dark:text-white hover:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
            >
              {loadingExtensions ? (
                <span className="text-gray-400">Loading...</span>
              ) : (
                <span className="truncate">{extensionLabel(selected)}</span>
              )}
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform ${showExtensionDropdown ? 'rotate-180' : ''}`}
              />
            </button>
            {showExtensionDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute z-20 mt-1 w-full min-w-[280px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden"
              >
                <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="search"
                      autoFocus
                      value={extensionSearch}
                      onChange={(event) => setExtensionSearch(event.target.value)}
                      placeholder="Search extension..."
                      className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {!extensionSearch.trim() && (
                    <button
                      type="button"
                      className={`flex items-center gap-2 w-full px-4 py-2.5 text-left font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        selected === ALL_EXTENSIONS ? 'bg-emerald-50 dark:bg-emerald-900/30' : ''
                      }`}
                      onClick={() => {
                        setSelected(ALL_EXTENSIONS);
                        setShowExtensionDropdown(false);
                      }}
                    >
                      <Users className="w-4 h-4 text-emerald-600" />
                      All extensions
                    </button>
                  )}
                  {filteredExtensions.length ? (
                    filteredExtensions.map((ext) => (
                      <button
                        type="button"
                        key={ext.id}
                        className={`block w-full px-4 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          selected === ext.number ? 'bg-emerald-50 dark:bg-emerald-900/30' : ''
                        }`}
                        onClick={() => {
                          setSelected(ext.number);
                          setShowExtensionDropdown(false);
                          setExtensionSearch('');
                        }}
                      >
                        <span className="text-gray-900 dark:text-white font-medium">{ext.number}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-2">
                          {ext.display_name || ext.username}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      No extensions found
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setActiveDatePreset(null);
                setStartDate(e.target.value);
              }}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setActiveDatePreset(null);
                setEndDate(e.target.value);
              }}
              className={inputClass}
            />
          </div>

          <div className="flex items-end">
            <Button
              variant="primary"
              onClick={generateReport}
              isLoading={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Generate Report
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">Quick select:</span>
            {(
              [
                ['thisMonth', 'This Month'],
                ['lastMonth', 'Last Month'],
                ['thisYear', 'This Year'],
                ['lastYear', 'Last Year'],
              ] as Array<[DatePreset, string]>
            ).map(([preset, label]) => (
              <button
                key={preset}
                type="button"
                onClick={() => applyDatePreset(preset)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activeDatePreset === preset
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            {(
              [
                ['Mobile', mobileRate, setMobileRate],
                ['Landline', landlineRate, setLandlineRate],
              ] as Array<[string, string, (value: string) => void]>
            ).map(([label, value, setValue]) => (
              <label key={label} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {label} rate
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="w-32 pl-7 pr-12 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">/min</span>
                </div>
              </label>
            ))}
            {(rates.mobilePerMinute !== STANDARD_RATES.mobilePerMinute ||
              rates.landlinePerMinute !== STANDARD_RATES.landlinePerMinute) && (
              <button
                type="button"
                onClick={() => {
                  setMobileRate(STANDARD_RATES.mobilePerMinute.toFixed(2));
                  setLandlineRate(STANDARD_RATES.landlinePerMinute.toFixed(2));
                }}
                className="px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400 hover:underline"
              >
                Reset to standard
              </button>
            )}
          </div>
        </div>
      </Card>

      {loading && (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Loader size="lg" />
            <span className="ml-4 text-gray-600 dark:text-gray-400">Fetching call records...</span>
          </div>
        </Card>
      )}

      {!loading && report && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Card>
            <div ref={resultsRef} className="scroll-mt-4" />
            {scope && (
              <button
                type="button"
                onClick={() => openExtension(null)}
                className="inline-flex items-center gap-1 mb-3 text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to all extensions
              </button>
            )}
            <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {scope ? extensionLabel(scope) : 'Call Costs'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {scope ? 'Call costs' : extensionLabel(report.extension)} ·{' '}
                  {new Date(report.startDate).toLocaleDateString()} -{' '}
                  {new Date(report.endDate).toLocaleDateString()}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => exportCsv(scope)}
                  disabled={visibleCalls.length === 0}
                  className="pdf-hide"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button
                  variant="primary"
                  onClick={() => exportPdf(scope)}
                  isLoading={exportingPdf === (scope ?? 'report')}
                  disabled={visibleCalls.length === 0 || exportingPdf !== null}
                  className="pdf-hide bg-emerald-600 hover:bg-emerald-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
              {summaryCards.map((card) => (
                <div key={card.label} className={`${card.className} rounded-lg p-4 text-center`}>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{card.label}</p>
                  <p className={`text-2xl font-bold ${card.valueClass}`}>{card.value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{card.detail}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">{costNote(summary)}</p>

            {allMode && !scope && extensionRows.length > 0 && (
              <div className="mb-8">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Cost by extension</h4>
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Click an extension to see its calls, or download its own CSV or PDF
                    </p>
                    <ColumnPicker table={extensionTable} />
                  </div>
                </div>
                <ReportTable
                  table={extensionTable}
                  rows={sortedExtensionRows}
                  rowKey={(row) => row.extension}
                  onRowClick={(row) => openExtension(row.extension)}
                  stretch={['extension']}
                  footer={{
                    ...extensionTotals,
                    totalCost: (
                      <span className="text-emerald-700 dark:text-emerald-400">{extensionTotals.totalCost}</span>
                    ),
                  }}
                  trailing={{
                    header: 'Download',
                    width: 136,
                    cell: (row) => (
                      <div className="inline-flex gap-1">
                        <button
                          type="button"
                          title={`Download CSV for ${row.label}`}
                          onClick={() => exportCsv(row.extension)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5" />
                          CSV
                        </button>
                        <button
                          type="button"
                          title={`Download PDF for ${row.label}`}
                          onClick={() => exportPdf(row.extension)}
                          disabled={exportingPdf !== null}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-50"
                        >
                          {exportingPdf === row.extension ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                          PDF
                        </button>
                      </div>
                    ),
                  }}
                />
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Call records</h4>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-700 p-1">
                  {(
                    [
                      ['outbound', `Outbound (${scopedOutboundCount})`],
                      ['all', `All calls (${scopedCalls.length})`],
                    ] as Array<[CallFilter, string]>
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setCallFilter(value)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        callFilter === value
                          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <ColumnPicker table={callTable} />
              </div>
            </div>

            {visibleCalls.length > 0 ? (
              <ReportTable
                table={callTable}
                rows={sortedCalls}
                rowKey={(call) => call.index}
                stretch={['to', 'from', 'extension', 'trunk', 'time']}
                footer={{
                  ...callTotals,
                  cost: <span className="text-emerald-700 dark:text-emerald-400">{callTotals.cost}</span>,
                }}
              />
            ) : (
              <div className="text-center py-12">
                <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  No {callFilter === 'outbound' ? 'outbound ' : ''}calls found for the selected period.
                </p>
              </div>
            )}
          </Card>
        </motion.div>
      )}
    </div>
  );
}
